import { createContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import type { User } from '../../types';

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Fetch user data from Firestore and custom claims
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          // Get custom claims from ID token
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const customClaims = idTokenResult.claims;

          if (userDoc.exists()) {
            const firestoreData = userDoc.data() as User;
            // Merge custom claims with Firestore data
            setUserData({
              ...firestoreData,
              isAdmin: customClaims.admin === true || customClaims.isAdmin === true || firestoreData.isAdmin === true,
            });
          } else {
            // Create user document if it doesn't exist
            const newUserData: any = {
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              role: 'user',
              isAdmin: customClaims.admin === true || customClaims.isAdmin === true,
              createdAt: serverTimestamp(),
            };

            // Only add name if it exists
            if (firebaseUser.displayName) {
              newUserData.name = firebaseUser.displayName;
            }

            await setDoc(userDocRef, newUserData);
            setUserData(newUserData as User);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string, name?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with name if provided
    if (name) {
      await updateProfile(userCredential.user, { displayName: name });
    }

    // Create user document in Firestore
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userDocRef, {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      name: name || null,
      role: 'user',
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });

    // Send verification email with redirect to portal
    const actionCodeSettings = {
      url: `${window.location.origin}/portal?emailVerified=true`,
    };
    await sendEmailVerification(userCredential.user, actionCodeSettings);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const sendVerificationEmail = async () => {
    if (user) {
      const actionCodeSettings = {
        url: `${window.location.origin}/portal?emailVerified=true`,
      };
      await sendEmailVerification(user, actionCodeSettings);
    }
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const isAdmin = userData?.isAdmin === true;

  const value: AuthContextType = {
    user,
    userData,
    loading,
    signIn,
    signUp,
    signOut,
    sendVerificationEmail,
    resetPassword,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
